/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/array-type */
import {ExclamationOutlined, SearchOutlined} from '@ant-design/icons';
import type {InputRef} from 'antd';
import {Button, Input, Table as TableAnt, Tag} from 'antd';
import {ColumnType, ColumnsType} from 'antd/es/table';
import moment from 'moment';
import React, {useContext, useEffect, useMemo, useRef, useState} from 'react';

import FilteredUnit from '../../constants/FilteredUnit';
import InformativeText from '../../constants/InformativeText';
import {SampleExcel} from '../../constants/SampleExcel';
import {ISelect} from '../../interface/ISelect';
import SelectedCountryContext from '../../pages/configurator/orgFacility/Constants/SelectedCountryContext';
import {uploadTypes} from '../../services/constants/uploadTypes';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import empCommuteAPI from '../../services/modules/IP/empCommuteAPI';
import designation from '../../services/modules/JSON/designation';
import genderJson from '../../services/modules/JSON/gender';
import modeOfTransportTrav from '../../services/modules/JSON/modeOfTransportByTravel';
import workingDays from '../../staticJson/workingDaysOfWeek.json';
import usePlatformStore from '../../store/platformStore/platformStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import {
  getStatusColor,
  hasUserRole,
  hasViewRole,
  UserRole
} from '../../utils/commonFunctions';
import {formatIndian} from '../../utils/formatIndian';
import {shouldBlockFree} from '../../utils/SubscriptionHelper';
import {objTypes} from '../DrawerForm/Constants/commonConstants';
import AllDataInfo from '../DrawerForm/Utils/AllDataInfo';
import DrawerForm from '../DrawerForm/Utils/DrawerForm';
import {
  facilityKeyLabel,
  yearKeyLabel
} from '../Forms/Constants/IP/commonKeyLabel';
import {EmpKey, EmpLabel} from '../Forms/Constants/IP/EmpKeyLabel';
import EmpForm from '../Forms/EmpForm';
import {
  checkerStatusKey,
  checkerStatusLabel
} from '../Forms/Utils/checkerStatusKeyLabel';
import JobsModal from '../Jobs/JobsModal';
import Loader from '../Loader/Loader';

import {FullInfo, makeCheckKeyLabel, Title} from './Constants/Common';
import {defaultCol} from './Interface/ITable';
import ActionsButton from './Utils/ActionsButton';
import TableTitleHeader from './Utils/TableTitleHeader';

const EmpTable: React.FC<{
  selectedYear: string;
  facilityId: string;
  isConfigurator?: boolean;
}> = ({selectedYear, facilityId, isConfigurator = false}) => {
  interface DataType {
    key: React.Key;
    _id: string;
    firstName: string;
    lastName: string;
    empCode: string;
    email: string;
    mobile: string;
    dateOfJoining: number;
    workingDaysInWeek: number;
    mode: number;
    distanceTravelled: number;
    homeAddr: string;
    officeAddr: string;
    status: string;
    comment: string;
  }

  const [dataSource, setDataSource] = useState<DataType[]>([]);

  const [showModel, setShowModel] = useState(false);
  const [showJobs, setShowJobs] = useState(false);
  const [editData, setEditData] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [refresh, setRefresh] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [allJSON, setAllJSON] = useState(false);
  const modeOfTransport = useJSONStore.getState().modeOfTransportTrav;
  const designationOptions = useJSONStore.getState().designation;
  const [showModelInfo, setShowModelInfo] = useState(false);
  const genderOptions = useJSONStore.getState().gender;
  const [facilityEfdb, setFacilityEfdb] = useState<any>({});

  const {selectedCountry, efdbData} = useContext(SelectedCountryContext);

  useEffect(() => {
    if (!facilityId || !selectedYear) {
      setDataSource([]);
    }
    if (facilityId && selectedYear && allJSON) {
      setIsRefresh(true);
      empCommuteAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
          setFacilityEfdb(
            efdbData?.[selectedYear]?.find((i: any) => {
              return i.facilityId === facilityId;
            })
          );
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
        });
    }
  }, [facilityId, selectedYear, allJSON]);

  useEffect(() => {
    // setIsRefresh(true);
    Promise.all([
      !isConfigurator && modeOfTransportTrav(),
      designation(),
      genderJson()
    ])
      .catch(() => {})
      .finally(() => {
        // setIsRefresh(false);
        setAllJSON(true);
      });
  }, []);

  useEffect(() => {
    if (refresh) {
      setIsRefresh(true);
      empCommuteAPI(
        APITypeEnum.GET_ALL,
        undefined,
        `${facilityId}/${selectedYear}`
      )
        .then((res) => {
          setDataSource(res ?? []);
        })
        .catch(() => {
          setDataSource([]);
        })
        .finally(() => {
          setIsRefresh(false);
          setRefresh(false);
        });
    }
  }, [refresh]);

  const handleDelete = (_id: string[] | string) => {
    empCommuteAPI(APITypeEnum.DELETE_ALL, {
      employeecommute: _id,
      year: selectedYear,
      facility: facilityId
    })
      .then(() => {
        setSelectedRowKeys([]);
        setRefresh(true);
      })
      .catch(() => {});
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const sorter = (key: keyof DataType) => {
    return {
      sorter: (a: DataType, b: DataType) => {
        const valueA = a[key];
        const valueB = b[key];
        if (typeof valueA === 'number' && typeof valueB === 'number') {
          return valueA - valueB;
        } else if (typeof valueA === 'string' && typeof valueB === 'string') {
          return valueA.localeCompare(valueB);
        }

        return 0;
      }
    };
  };

  const searchInput = useRef<InputRef>(null);

  const SearchFilter = (
    title: string,
    dataIndex: keyof any,
    option: ISelect[] | undefined = undefined
  ): defaultCol & ColumnType<any> => {
    return {
      filterDropdown: ({selectedKeys, setSelectedKeys, confirm}) => {
        return (
          <div
            style={{padding: 8}}
            onKeyDown={(e) => {
              e.stopPropagation();
            }}
          >
            <Input
              ref={searchInput}
              value={selectedKeys[0]}
              placeholder={`Search ${title}`}
              onChange={(e) => {
                setSelectedKeys(e.target.value ? [e.target.value] : []);
              }}
              onPressEnter={() => {
                confirm();
              }}
              onBlur={() => {
                confirm();
              }}
              allowClear
            />
          </div>
        );
      },
      filterIcon: <SearchOutlined />,
      onFilter: (value: any, record) => {
        const fieldValue = record[dataIndex];
        if (dataIndex === EmpKey.DATE_JOINING) {
          return moment.isMoment(fieldValue)
            ? fieldValue?.format('DD-MM-YYYY').includes(value)
            : false;
        }

        if (option) {
          const filtered = option.filter((item) => {
            if (typeof item?.label === 'string') {
              return item?.label
                ?.toString()
                ?.toLowerCase()
                ?.includes(value?.toString()?.toLowerCase());
            } else {
              return [];
            }
          });

          return filtered?.some((item) => {
            return item?.value === record[dataIndex];
          });
        } else {
          return record[dataIndex]
            ?.toString()
            ?.toLowerCase()
            ?.includes(value?.toString()?.toLowerCase());
        }
      },
      onFilterDropdownOpenChange: (visible) => {
        if (visible) {
          setTimeout(() => searchInput.current?.select(), 100);
        }
      }
    };
  };

  const empCommuteKeyLabel = [
    {
      key: yearKeyLabel.key,
      label: yearKeyLabel.label,
      type: objTypes.string
    },
    {
      key: facilityKeyLabel.key,
      label: facilityKeyLabel.label,
      type: objTypes.facilityFormPreview,
      relatedKey: yearKeyLabel.key,
      isMaker: hasUserRole(UserRole.Maker_Role)
    },
    {
      key: EmpKey.FIRST_NAME,
      label: EmpLabel.FIRST_NAME,
      type: objTypes.string
    },
    {
      key: EmpKey.LAST_NAME,
      label: EmpLabel.LAST_NAME,
      type: objTypes.string
    },
    {
      key: EmpKey.GENDER,
      label: EmpLabel.GENDER,
      type: objTypes.json,
      hidden: !isConfigurator,
      format: genderOptions
    },
    {
      key: EmpKey.EMP_CODE,
      label: EmpLabel.EMP_CODE,
      type: objTypes.string
    },
    {
      key: EmpKey.EMAIL,
      label: EmpLabel.EMAIL,
      type: objTypes.string
    },
    {
      key: EmpKey.MOBILE,
      label: EmpLabel.MOBILE,
      type: objTypes.string
    },
    {
      key: EmpKey.DATE_JOINING,
      label: EmpLabel.DATE_JOINING,
      type: objTypes.date,
      format: 'DD-MM-YYYY'
    },
    {
      key: EmpKey.WORKING_DAYS,
      label: EmpLabel.WORKING_DAYS,
      type: objTypes.number,
      hidden: isConfigurator
    },
    {
      key: EmpKey.MODE,
      label: EmpLabel.MODE,
      type: objTypes.json,
      format: modeOfTransport,
      hidden: isConfigurator
    },
    {
      key: EmpKey.DISTANCE_TRAVELLED,
      label: EmpLabel.DISTANCE_TRAVELLED,
      type: objTypes.number,
      hidden: isConfigurator,
      unit: FilteredUnit(selectedCountry).distance
    },
    {
      key: EmpKey.HOME_ADD,
      label: EmpLabel.HOME_ADD,
      type: objTypes.string,
      hidden: isConfigurator
    },
    {
      key: EmpKey.OFFICE_ADD,
      label: EmpLabel.OFFICE_ADD,
      type: objTypes.string,
      hidden: isConfigurator
    },
    {
      key: EmpKey.DESIGNATION,
      label: EmpLabel.DESIGNATION,
      type: objTypes.json,
      hidden: !isConfigurator,
      format: designationOptions
    },
    ...(hasUserRole(UserRole.Checker_Role) ||
    (hasUserRole(UserRole.Both_role) && !isConfigurator)
      ? [
          {
            key: checkerStatusKey.STATUS,
            label: checkerStatusLabel.STATUS,
            type: objTypes.status
          },
          {
            key: checkerStatusKey.COMMENT,
            label: checkerStatusLabel.COMMENT,
            type: objTypes.comment
          }
        ]
      : [])
  ];

  const columns: (defaultCol & ColumnsType<DataType>[0])[] = useMemo(() => {
    return [
      {
        title: EmpLabel.FIRST_NAME,
        dataIndex: EmpKey.FIRST_NAME,
        editable: true,
        // ...sorter(EmpKey.FIRST_NAME as keyof DataType),
        // fixed: 'left',
        width: '200px',
        ...SearchFilter(
          EmpLabel.FIRST_NAME,
          EmpKey.FIRST_NAME as keyof DataType
        )
      },
      {
        title: EmpLabel.LAST_NAME,
        dataIndex: EmpKey.LAST_NAME,
        width: '200px',
        editable: true,
        // ...sorter(EmpKey.LAST_NAME as keyof DataType),
        ...SearchFilter(EmpLabel.LAST_NAME, EmpKey.LAST_NAME as keyof DataType)
      },

      {
        title: EmpLabel.EMP_CODE,
        dataIndex: EmpKey.EMP_CODE,
        editable: true,
        ...SearchFilter(EmpLabel.EMP_CODE, EmpKey.EMP_CODE as keyof DataType),
        width: '200px',
        render: (value) => {
          return value || '-';
        }
      },
      // {
      //   title: EmpLabel.EMAIL,
      //   dataIndex: EmpKey.EMAIL,
      //   editable: true,
      //   // ...sorter(EmpKey.EMAIL as keyof DataType),
      //   width: '200px',
      //   ...SearchFilter(EmpLabel.EMAIL, EmpKey.EMAIL as keyof DataType)
      // },
      // {
      //   title: EmpLabel.MOBILE,
      //   dataIndex: EmpKey.MOBILE,
      //   editable: true,
      //   width: '200px',
      //   type: 'number'
      // },
      {
        title: EmpLabel.GENDER,
        dataIndex: EmpKey.GENDER,
        editable: true,
        hidden: !isConfigurator,
        type: 'number',
        width: '200px',
        render: (value) => {
          return (
            <>
              {
                genderOptions.find((item) => {
                  return item?.value === value;
                })?.label
              }
            </>
          );
        }
      },
      {
        title: EmpLabel.DATE_JOINING,
        dataIndex: EmpKey.DATE_JOINING,
        editable: true,
        width: '200px',
        ...SearchFilter(
          EmpLabel.DATE_JOINING,
          EmpKey.DATE_JOINING as keyof DataType
        ),
        render: (value) => {
          return <>{value?.format('DD-MM-YYYY')}</>;
        }
      },
      {
        title: EmpLabel.WORKING_DAYS,
        dataIndex: EmpKey.WORKING_DAYS,
        editable: true,
        width: '200px',
        type: 'select',
        hidden: isConfigurator,
        filterer: workingDays,
        filters: workingDays?.map((item) => {
          return {text: item.label, value: item.value};
        }),
        onFilter: (value, record) => {
          return record[EmpKey.WORKING_DAYS as keyof DataType] === value;
        },
        render: (value) => {
          return (
            <>
              {workingDays?.find((item) => {
                return item.value === value;
              })?.label ?? '-'}
            </>
          );
        }
      },
      {
        title: EmpLabel.MODE,
        dataIndex: EmpKey.MODE,
        editable: true,
        hidden: isConfigurator,
        width: '200px',
        type: 'select',
        ...SearchFilter(EmpLabel.MODE, EmpKey.MODE, modeOfTransport),
        render: (value: any) => {
          return (
            <>
              {modeOfTransport?.find((item) => {
                return (
                  item.value === value && item.dbName === facilityEfdb.scope3
                );
              })?.label ?? '-'}
            </>
          );
        }
      },
      {
        title: EmpLabel.DISTANCE_TRAVELLED,
        dataIndex: EmpKey.DISTANCE_TRAVELLED,
        editable: true,
        hidden: isConfigurator,
        type: 'number',
        width: '200px',
        render: (value) => {
          return value
            ? `${formatIndian(value)} ${FilteredUnit(selectedCountry).distance}`
            : '-';
        }
      },
      {
        title: EmpLabel.HOME_ADD,
        dataIndex: EmpKey.HOME_ADD,
        editable: true,
        hidden: isConfigurator,
        width: '200px',
        render: (value) => {
          const isNullOrEmpty = value === null || value === '';

          return isNullOrEmpty ? '-' : value;
        }
      },
      {
        title: EmpLabel.OFFICE_ADD,
        dataIndex: EmpKey.OFFICE_ADD,
        editable: true,
        hidden: isConfigurator,
        width: '200px',
        render: (value) => {
          const isNullOrEmpty = value === null || value === '';

          return isNullOrEmpty ? '-' : value;
        }
      },
      {
        title: EmpLabel.DESIGNATION,
        dataIndex: EmpKey.DESIGNATION,
        editable: true,
        hidden: !isConfigurator,
        type: 'select',

        ...SearchFilter(
          EmpLabel.DESIGNATION,
          EmpKey.DESIGNATION as keyof DataType,
          designationOptions
        ),
        render: (value: any) => {
          return (
            <>
              {
                designationOptions?.find((item) => {
                  return item?.value === value;
                })?.label
              }
            </>
          );
        },
        width: '200px'
      },
      ...((hasUserRole(UserRole.Maker_Role) ||
        hasUserRole(UserRole.Both_role) ||
        hasViewRole(UserRole.View_Only)) &&
      !isConfigurator
        ? [
            {
              title: makeCheckKeyLabel.STATUS_COMMENTS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <div
                    style={{
                      fontWeight: 700,
                      color: colorText.color,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}{' '}
                    {value === 'rejected' && record?.comment && (
                      <span className="customComment"> - {record.comment}</span>
                    )}
                  </div>
                );
              }
            }
          ]
        : []),
      ...(!isConfigurator &&
      (hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role))
        ? [
            {
              title: makeCheckKeyLabel.STATUS.label,
              dataIndex: 'status',
              editable: true,
              type: 'select',
              render: (value: any, record: any) => {
                const colorText = getStatusColor(value);

                return (
                  <Tag
                    onClick={() => {
                      if (shouldBlockFree()) {
                        usePlatformStore
                          .getState()
                          .setOpenOnFreeTrialMessageModel(true);

                        return;
                      }
                      setShowModelInfo(true);
                      setEditData(record);
                      setIsEdit(true);
                    }}
                    color={colorText.color}
                    style={{
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    {colorText.text}
                  </Tag>
                );
              }
            }
          ]
        : []),
      ...(!isConfigurator &&
      (hasUserRole(UserRole.Checker_Role) || hasUserRole(UserRole.Both_role))
        ? [
            {
              title: FullInfo.title,
              dataIndex: 'FullInfo',
              editable: true,
              type: 'select',
              render: (_: any, record: any) => {
                return (
                  <Button
                    onClick={() => {
                      if (shouldBlockFree()) {
                        usePlatformStore
                          .getState()
                          .setOpenOnFreeTrialMessageModel(true);

                        return;
                      }
                      setShowModelInfo(true);
                      setIsEdit(true);
                      setEditData(record);
                    }}
                    shape="circle"
                    style={{
                      marginRight: 16,
                      cursor: 'pointer',
                      color: '#435B37',
                      background: '#F5FBF3',
                      border: 0
                    }}
                  >
                    <ExclamationOutlined />
                  </Button>
                );
              }
            }
          ]
        : []),
      ...(hasUserRole(UserRole.Maker_Role) || hasUserRole(UserRole.Both_role)
        ? [
            {
              title: Title.actionTitle,
              dataIndex: 'operation',
              type: '' as const,
              fixed: 'right' as const,
              width: '200px',
              render: (value: any, record: any) =>
                dataSource.length >= 1 ? (
                  <ActionsButton
                    record={record}
                    setShowModel={setShowModel}
                    setEditData={setEditData}
                    setIsEdit={setIsEdit}
                    handleDelete={handleDelete}
                    isConfigurator={isConfigurator}
                    noDelete={!isConfigurator}
                  />
                ) : null
            }
          ]
        : [])
    ];
  }, [dataSource]);

  return (
    <>
      <div>
        <TableTitleHeader
          title="Employee Info"
          filter={{facilityId, selectedYear}}
          addButton={{
            setShowModel,
            setIsEdit,
            buttonTitle: 'Add Employee'
          }}
          sampleDownload={{href: SampleExcel.empTravel}}
          jobs={{setShowJobs, uploadType: uploadTypes.TravelEmp}}
          deleteButton={{selectedRowKeys, handleDelete}}
          refresh={{setRefresh}}
          tooltip={InformativeText.IP.travel.emp.empTravel}
          shouldEdit={
            isConfigurator && !hasUserRole(UserRole.View_Only)
            // ? !hasUserRole(UserRole.View_Only)
            // : hasUserRole(UserRole.Maker_Role) ||
            //   hasUserRole(UserRole.Both_role)
          }
          showAddOnly={!isConfigurator}
        />
        <JobsModal
          open={showJobs}
          onCancel={() => {
            setShowJobs(false);
            setRefresh(true);
          }}
          type={uploadTypes.TravelEmp}
          details={`${facilityId}/${selectedYear}`}
        />

        <TableAnt
          // getPopupContainer={(triggerNode) =>
          //   triggerNode.closest(
          //     '.ant-modal, .ant-drawer, [style*="position"]'
          //   ) as HTMLElement
          // }
          rowSelection={
            isConfigurator ??
            (hasUserRole(UserRole.Maker_Role) ||
              hasUserRole(UserRole.Both_role))
              ? {
                  type: 'checkbox',
                  selectedRowKeys,
                  onChange: (
                    selectedKey: React.Key[],
                    selectedRow: DataType[]
                  ) => {
                    setSelectedRowKeys(
                      selectedRow.map((item) => {
                        return item._id;
                      })
                    );
                  }
                }
              : undefined
          }
          rowKey="_id"
          dataSource={dataSource}
          columns={columns as any}
          scroll={{x: 'auto'}}
          loading={{
            indicator: <Loader />,
            spinning: allJSON ? isRefresh : true
          }}
          tableLayout="auto"
        />
        <DrawerForm
          open={showModel}
          onCancel={() => {
            setShowModel(false);
          }}
          setRefresh={setRefresh}
          data={isEdit ? editData : undefined}
          FormComponent={<EmpForm isConfigurator={isConfigurator} />}
          API={empCommuteAPI}
          idKey={`${facilityId}/${selectedYear}`}
          table={true}
          stepsTitle={['Configure Details', 'Employee Information']}
          formPreview={empCommuteKeyLabel}
          isConfigurator={isConfigurator}
        />
      </div>
      <DrawerForm
        open={showModelInfo}
        onCancel={() => {
          setShowModelInfo(false);
        }}
        setRefresh={setRefresh}
        API={empCommuteAPI}
        data={isEdit ? editData : undefined}
        FormComponent={
          <AllDataInfo formData={editData} formPreview={empCommuteKeyLabel} />
        }
        isChecker={true}
        isAction={false}
        stepsTitle={['Status & Comments']}
        formPreview={empCommuteKeyLabel}
        isConfigurator={isConfigurator}
      />
    </>
  );
};

export default EmpTable;
